import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { claimPaymentActions } from 'store';
import { FindLoad, FindSuccessItem, renderClientName, useModal, useSecondaryModal } from 'utils';
import {
    AccordionFullTable,
    AddCircleBlue,
    CustomDelete,
    DateTypeSelector,
    MarkSecondaryBilling,
    NotError,
    SelectAutocompletePagination,
    SingleSearch,
} from 'components';
import { linkedClaimTableInfo } from './constants';
import { claimPostStyles } from '../../styles';
import { ReceivableBillTable } from 'fragments';
import { SelectClaimPayment } from '../core';

const EDIT_CLAIM_ACTION_TYPE = 'EDIT_CLAIM_PAYMENT_INFO';
const REMOVE_CLAIM_FROM_REMITTANCE = 'REMOVE_CLAIM_FROM_REMITTANCE';

const errorMessages = {
    // paidIsGreat: 'Allowed amount cannot exceed Paid amount.',
    // notAllowed: 'Please ensure that the total sum of the Deductible, Copay, and Coinsurance amounts entered do not exceed the Allowed AMT.',
    paidIsGreat: 'Paid amount cannot exceed Allowed.',
    balanceError: 'PaidAMT cannot exceed the Balance.',
    notAllowed: 'PaidAMT cannot exceed Pmt Open Amount.',
    openAmtNotAllowed: 'The total sum of Deduct., Copay, CoINS and PaidAMT cannot exceed the Allowed amount.',
};

const errorEnums = {
    openAmtNotAllowed: 'openAmtNotAllowed',
    paidIsGreat: 'paidIsGreat',
    notAllowed: 'notAllowed',
    balanceError: 'balanceError',
};

export const LinkedClaims = ({}) => {
    const remittanceById = useSelector((state) => state.claimPayment.remittanceById);
    const remittanceByIdClaims = useSelector((state) => state.claimPayment.remittanceByIdClaims);
    const classes = claimPostStyles();
    const params = useParams();
    const openAmt = remittanceById?.openAmount;
    const dispatch = useDispatch();
    const [activeRow, setActiveRow] = useState();
    const [inputs, setInputs] = useState({});
    const [error, setError] = useState('');
    const [sendParams, setSendParams] = useState({ skip: 0, limit: 50 });
    const checkError = error;
    const loadSave = FindLoad(EDIT_CLAIM_ACTION_TYPE);
    const editSuccess = FindSuccessItem(EDIT_CLAIM_ACTION_TYPE);
    const currentInput = useRef();
    const { open, close } = useModal();
    const { openSecondary } = useSecondaryModal();

    const getRemittanceClaims = () => {
        const reqParams = {
            ...sendParams,
        };
        delete reqParams.activeTab;
        dispatch(claimPaymentActions.getRemittanceByIdClaims(params?.id, reqParams));
    };

    useEffect(() => {
        getRemittanceClaims();
    }, [sendParams]);

    useEffect(() => {
        if (editSuccess) {
            setActiveRow(null);
            setError('');
        }
    }, [editSuccess]);

    const handleAction = (item, name) => {
        const reqParams = {
            ...sendParams,
        };
        delete reqParams.activeTab;

        if (name === 'edit') {
            setActiveRow(item.id);
            const claimBills = [];
            item?.bills.forEach((i) => {
                claimBills.push({
                    id: i?.id,
                    allowedAMT: i?.allowedAMT,
                    deductible: i?.deductible,
                    copay: i?.copay,
                    coIns: i?.coIns,
                    paidAMT: i?.paidByThis,
                    payorPaid: i?.payorPaid,
                    payorBalance: i?.payorBalance,
                });
            });
            setInputs(claimBills);
            currentInput?.current?.focus();
        }
        if (name === 'save') {
            const body = {
                claimId: item?.id,
                remittanceId: remittanceById?.id,
                body: {
                    claimBills: [...inputs],
                },
            };
            if (!error) {
                dispatch(claimPaymentActions.editClaimPaymentInfo(body, null, remittanceById?.id, reqParams));
            } else {
                toast(<NotError type={'error'} errMessage={errorMessages?.[error]} />);
            }
        }
        if (name === 'close') {
            setActiveRow(null);
            setError('');
        }
        if (name === 'remove') {
            open(
                <CustomDelete
                    info="Remove Claim?"
                    text="This will permanently remove the claim from the payment. This action cannot be undone."
                    handleDel={() =>
                        dispatch(
                            claimPaymentActions.removeClaimFromRemittance(
                                {
                                    remittanceId: remittanceById?.id,
                                    claimId: item?.id,
                                },
                                reqParams
                            )
                        )
                    }
                    actionType={REMOVE_CLAIM_FROM_REMITTANCE}
                    innerText={'Remove'}
                />
            );
        }

        // if (name === 'secondaryBilling') {
        //     open(
        //         <MarkSecondaryBilling
        //             afterSuccess={() => getRemittanceClaims()}
        //             mark={!item?.secondaryBilling?.secondaryBillingState}
        //             claim={item}
        //         />
        //     );
        // }
    };

    const handleChange = (e, key, item) => {
        const allObj = [...inputs];
        const current = allObj[key];
        const value = e.target.value;
        const convertedValue = value ? parseFloat(value.replace(/[$,]/g, '')) : 0;
        const deductible = e?.target?.name === 'deductible' ? convertedValue : current?.deductible;
        const copay = e?.target?.name === 'copay' ? convertedValue : current?.copay;
        const coIns = e?.target?.name === 'coIns' ? convertedValue : current?.coIns;
        const paidAMT = e?.target?.name === 'paidAMT' ? convertedValue : current?.paidAMT;
        const allowed = e?.target?.name === 'allowedAMT' ? convertedValue : current?.allowedAMT;

        setError('');
        allObj[key][e?.target?.name] = convertedValue;
        setInputs(allObj);

        const enteredValues = Number(deductible + copay + coIns + paidAMT).toFixed(2);
        const totalWithPayor = Number(enteredValues + (current?.payorPaid || 0)).toFixed(2);
        const calculatedBalance = Number(
            item?.allowedAMT >= allowed
                ? item?.payorBalance + item?.paidByThis - (item.allowedAMT - allowed)
                : item?.payorBalance + item?.paidByThis + (allowed - item.allowedAMT)
        ).toFixed(2);

        if (totalWithPayor > allowed) {
            setError(errorEnums.openAmtNotAllowed);
        } else if (paidAMT > Number(openAmt + item?.paidByThis).toFixed(2)) {
            setError(errorEnums.notAllowed);
        } else if (paidAMT > calculatedBalance) {
            setError(errorEnums.balanceError);
        } else {
            setError(null);
        }
    };

    // const handleChange = (e, key, item) => {
    //     // dispatch(httpRequestsOnErrorsActions.removeError(errType));
    //     const allObj = [...inputs];
    //     const current = allObj[key];
    //     const value = e.target.value;
    //     const convertedValue = value ? parseFloat(value.replace(/[$,]/g, '')) : 0;
    //     const deductible = e?.target?.name === 'deductible' ? convertedValue : current?.deductible;
    //     const copay = e?.target?.name === 'copay' ? convertedValue : current?.copay;
    //     const coIns = e?.target?.name === 'coIns' ? convertedValue : current?.coIns;
    //     const paidAMT = e?.target?.name === 'paidAMT' ? convertedValue : current?.paidAMT;
    //     const allowed = e?.target?.name === 'allowedAMT' ? convertedValue : current?.allowedAMT;
    //
    //     setError('');
    //     allObj[key][e?.target?.name] = convertedValue;
    //     setInputs(allObj);
    //
    //     const totalEntered = deductible + copay + coIns + paidAMT;
    //     const totalWithPayor = totalEntered + (current?.payorPaid || 0);
    //
    //     if ((openAmt + paidAMT) < totalEntered) {
    //         setError(totalWithPayor > allowed ? errorEnums.notAllowed : errorEnums.openAmtNotAllowed);
    //     } else if (totalWithPayor > allowed) {
    //         setError(paidAMT > allowed ? errorEnums.paidIsGreat : errorEnums.notAllowed);
    //     } else {
    //         setError(null);
    //     }
    // };

    return (
        <div>
            <div style={{ marginTop: 16 }}>
                <div className="space-between">
                    <div className="select-claim-filters flex-align-end" style={{ gap: 16 }}>
                        <SelectAutocompletePagination
                            outLabel={'Client'}
                            name={'client'}
                            handleSelect={(e) => {
                                const params = {
                                    ...sendParams,
                                };
                                e?.target?.value ? (params.client = e?.target?.value) : delete params['client'];
                                setSendParams(params);
                            }}
                            placeholder={'All'}
                            defaultValue={sendParams?.client}
                            renderValue={(i) => renderClientName(i)}
                            type={'client'}
                            uiType={'tableFilter'}
                            noError={true}
                        />
                        <DateTypeSelector
                            startName={'from'}
                            endName={'to'}
                            outLabel={'DOS'}
                            filters={sendParams}
                            handleGetDates={(e) => {
                                const params = {
                                    ...sendParams,
                                    from: e?.from,
                                    to: e?.to,
                                };
                                setSendParams(params);
                            }}
                        />
                        <SingleSearch
                            placeholder={'Search by Claim ID'}
                            searchTerm={sendParams?.searchValue}
                            setSearchTerm={(value) => {
                                const params = { ...sendParams };
                                if (value) {
                                    params.searchKey = 'DISPLAY_ID';
                                    params.searchValue = value;
                                } else {
                                    delete params.searchKey;
                                    delete params.searchValue;
                                }
                                setSendParams(params);
                            }}
                        />
                    </div>

                    {remittanceById?.status !== 'VOID' && (
                        <AddCircleBlue
                            text={'Add Claim'}
                            handleClick={() => {
                                const reqParams = {
                                    ...sendParams,
                                };
                                delete reqParams.activeTab;
                                openSecondary(<SelectClaimPayment reqParams={sendParams} />, {
                                    styles: {
                                        bodyStyle: {
                                            maxHeight: '93vh',
                                            height: '93vh',
                                        },
                                    },
                                });
                            }}
                        />
                    )}
                </div>

                <div className="linked-claim-table">
                    <AccordionFullTable
                        getParams={setSendParams}
                        savedParams={sendParams}
                        handleAccordion={() => {
                            setError('');
                            setActiveRow(null);
                        }}
                        defaultOpen={activeRow && remittanceByIdClaims?.claims?.findIndex((item) => item?.id === activeRow)}
                        margin={'16px 0 0 0'}
                        tableInfo={linkedClaimTableInfo(
                            classes,
                            handleAction,
                            handleChange,
                            currentInput,
                            activeRow,
                            inputs,
                            loadSave,
                            checkError,
                            remittanceById,
                            getRemittanceClaims,
                        )}
                        list={remittanceByIdClaims?.claims}
                        listCount={remittanceByIdClaims?.count}
                        onRowClick={(item) => {
                            open(<ReceivableBillTable defaultBill={item} from={'remittance'} />);
                        }}
                    />
                </div>
            </div>
        </div>
    );
};
